// Pick to tote (special pick queue 4) order list. Modeled on
// QueueOrderList, with three differences: the list is TIER-scoped (the
// page's High/Low toggle drives the `ready` flag — queue-4 waves honour
// the tier filter, unlike queues 1-3), there is no queue-level Start
// (claim-only: the picker taps a specific order), and rows with a note
// from the admin Orders page show an info toggle that pops the note up.
import { useCallback, useEffect, useState } from "react";
import { Clock3, Info, Loader2, Package2, X } from "lucide-react";
import { toast } from "react-toastify";
import { apiFetch } from "../../api/apiFetch";
import { useTranslation } from "../../i18n";
import FlowLoader from "../FlowLoader";
import { PICKING_POLL_MS } from "./constants";

export interface PickToToteOrder {
    order_id: string;
    order_number: string | null;
    channel_created_at: string;
    total_items: number;
    ready: boolean;
    note: string | null;
}

interface OrdersResponse {
    tier: "low" | "high";
    orders: PickToToteOrder[];
}

interface Props {
    tier: "low" | "high";
    onWaveStarted: () => void;
    // Surfaced so the page can grey its tier toggle while a start is in
    // flight.
    onBusyChange?: (busy: boolean) => void;
}

const formatAgo = (
    raw: string,
    t: (key: string, vars?: Record<string, string>) => string,
): string => {
    const timestamp = new Date(raw).getTime();
    if (!Number.isFinite(timestamp)) return "";
    const ms = Math.max(0, Date.now() - timestamp);
    if (ms < 60_000) return t("picking_ago_just_now");
    const minutes = Math.floor(ms / 60_000);
    if (minutes < 60) return t("picking_ago_minutes", { m: String(minutes) });
    const hours = Math.floor(minutes / 60);
    return t("picking_ago_hours", { h: String(hours) });
};

const PickToToteOrderList = ({ tier, onWaveStarted, onBusyChange }: Props) => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState<PickToToteOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [startingOrderId, setStartingOrderId] = useState<string | null>(null);
    const [noteOrderId, setNoteOrderId] = useState<string | null>(null);
    const busy = startingOrderId !== null;

    useEffect(() => { onBusyChange?.(busy); }, [busy, onBusyChange]);

    const loadOrders = useCallback(async (showLoader = false) => {
        if (showLoader) setLoading(true);
        try {
            const res = await apiFetch(`/picking/pick-to-tote/orders?tier=${tier}`, {});
            if (!res.ok) return;
            const body: OrdersResponse = await res.json();
            setOrders(Array.isArray(body.orders) ? body.orders : []);
        } catch {
            // Transient failures are retried by the light poll below.
        } finally {
            if (showLoader) setLoading(false);
        }
    }, [tier]);

    useEffect(() => {
        let alive = true;
        const tick = async (showLoader = false) => {
            if (!alive) return;
            await loadOrders(showLoader);
        };
        void tick(true);
        const intervalId = window.setInterval(() => { void tick(); }, PICKING_POLL_MS);
        return () => {
            alive = false;
            window.clearInterval(intervalId);
        };
    }, [loadOrders]);

    const startWave = async (orderId: string) => {
        if (busy) return;
        setStartingOrderId(orderId);
        let handedOff = false;
        try {
            const res = await apiFetch("/picking/wave/start", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    pickType: "large",
                    pickQueue: 4,
                    orderId,
                    tier,
                    ageFilter: "all",
                    deliveryFilter: "all",
                    channelFilter: "all",
                    ageFrom: null,
                    ageTo: null,
                    dueFilter: false,
                }),
            });
            if (res.ok) {
                handedOff = true;
                onWaveStarted();
                return;
            }
            const errorBody = await res.json().catch(() => ({}));
            const code = errorBody?.code as string | undefined;
            if (code === "ALREADY_ACTIVE") {
                handedOff = true;
                toast.info(t("picking_already_active_wave"), { toastId: "picking-p2t-already-active" });
                onWaveStarted();
                return;
            }
            if (code === "NO_CANDIDATES" || code === "NO_MATCHING" || code === "NO_SELECTED") {
                // Claim race (another picker grabbed the order) or the
                // stock just moved — refresh so the list tells the truth.
                toast.info(t("picking_no_orders_to_pick"), { toastId: "picking-p2t-no-orders" });
                void loadOrders();
                return;
            }
            if (code === "BUILD_CONFLICT") {
                toast.error(t("picking_start_conflict"), { toastId: "picking-p2t-conflict" });
                return;
            }
            toast.error(t("picking_start_failed"), { toastId: "picking-p2t-start-fail" });
        } catch {
            toast.error(t("picking_start_failed"), { toastId: "picking-p2t-start-net" });
        } finally {
            if (!handedOff) setStartingOrderId(null);
        }
    };

    const noteOrder = noteOrderId
        ? orders.find(order => order.order_id === noteOrderId) ?? null
        : null;

    // The page (pick-to-tote.tsx) supplies the picking-selector /
    // picking-queue-orders wrapper — this renders only the body.
    return (
        <div>
            {loading ? (
                <div className="picking-queue-loading"><FlowLoader /></div>
            ) : orders.length === 0 ? (
                <div className="picking-selector-empty">{t("pick_to_tote_empty")}</div>
            ) : (
                <ul className="picking-queue-order-list">
                    {orders.map(order => {
                        const age = formatAgo(order.channel_created_at, t);
                        const isStarting = startingOrderId === order.order_id;
                        return (
                            <li
                                key={order.order_id}
                                className={`picking-queue-order-row${order.ready ? "" : " picking-queue-order-row--not-ready"}`}
                            >
                                <div className="picking-queue-order-main">
                                    <strong>
                                        {order.order_number || order.order_id}
                                        {order.note && (
                                            <button
                                                type="button"
                                                className="pick-to-tote-note-btn"
                                                onClick={() => setNoteOrderId(order.order_id)}
                                                aria-label={t("pick_to_tote_note_title")}
                                            >
                                                <Info size={17} aria-hidden="true" />
                                            </button>
                                        )}
                                    </strong>
                                    <div className="picking-queue-order-meta">
                                        {age && <span><Clock3 size={15} aria-hidden="true" />{age}</span>}
                                        <span>
                                            <Package2 size={15} aria-hidden="true" />
                                            {t("picking_queue_items", { count: String(order.total_items) })}
                                        </span>
                                    </div>
                                    {!order.ready && (
                                        <span className="picking-queue-not-ready">{t("picking_queue_not_ready")}</span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-primary picking-queue-pick-btn"
                                    onClick={() => void startWave(order.order_id)}
                                    disabled={busy || !order.ready}
                                >
                                    {isStarting && <Loader2 size={17} className="picking-starting-spinner" aria-hidden="true" />}
                                    {t("picking_queue_pick")}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {noteOrder && (
                <div
                    className="modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label={t("pick_to_tote_note_title")}
                    onClick={() => setNoteOrderId(null)}
                >
                    <div className="modal-card pick-to-tote-note-card" onClick={(e) => e.stopPropagation()}>
                        <div className="pick-to-tote-note-head">
                            <strong>{noteOrder.order_number || noteOrder.order_id}</strong>
                            <button
                                type="button"
                                className="page-back-btn"
                                onClick={() => setNoteOrderId(null)}
                                aria-label={t("close")}
                            >
                                <X size={18} aria-hidden="true" />
                            </button>
                        </div>
                        <p className="pick-to-tote-note-body">{noteOrder.note}</p>
                    </div>
                </div>
            )}

            {busy && (
                <div className="picking-building-overlay" role="status" aria-live="polite" aria-busy="true">
                    <Loader2 className="picking-starting-spinner" size={56} strokeWidth={2.25} aria-hidden="true" />
                </div>
            )}
        </div>
    );
};

export default PickToToteOrderList;
